import React from 'react'
import { UIRadioGroupBase as RadioGroup, UIRadioGroupItemBase as RadioGroupItem } from './ui-radio-group'
import { UILabel } from './ui-label'
import { UITypography } from '../display/ui-typography'
import { cn } from '../../lib/utils'
import { UIBox } from '../layout/ui-box'

export interface UIRadioGroupCardOption {
  value: string
  title: string
  description?: string
  icon?: React.ReactNode
  id?: string
  disabled?: boolean
  badge?: string
  price?: string
}

export interface UIRadioGroupCardProps extends React.ComponentProps<typeof RadioGroup> {
  options: UIRadioGroupCardOption[]
  label?: string
  description?: string
  /**
   * 카드 크기
   */
  size?: 'sm' | 'default' | 'lg'
  /**
   * 오류 메시지
   */
  error?: string
  /**
   * 필수 여부
   */
  required?: boolean
}

const sizeVariants = {
  sm: {
    card: 'p-3',
    title: 'text-sm font-medium leading-4',
    description: 'text-xs leading-4',
    icon: 'w-4 h-4',
    label: 'text-sm leading-4',
    mainDescription: 'text-xs leading-4',
    error: 'text-xs leading-4',
    price: 'text-sm leading-4'
  },
  default: {
    card: 'p-4',
    title: 'text-base font-semibold leading-5',
    description: 'text-sm leading-4',
    icon: 'w-5 h-5',
    label: 'text-base leading-5',
    mainDescription: 'text-sm leading-4',
    error: 'text-sm leading-4',
    price: 'text-lg leading-5'
  },
  lg: {
    card: 'p-6',
    title: 'text-lg font-semibold leading-6',
    description: 'text-base leading-5',
    icon: 'w-6 h-6',
    label: 'text-lg leading-6',
    mainDescription: 'text-base leading-5',
    error: 'text-sm leading-4',
    price: 'text-xl leading-6'
  }
}

/**
 * 카드 스타일 라디오 그룹 컴포넌트
 * 카드 형태로 선택 옵션을 표시하며, 제목, 설명, 아이콘, 가격 등을 지원합니다.
 */
export const UIRadioGroupCard = React.forwardRef<
  React.ComponentRef<typeof RadioGroup>,
  UIRadioGroupCardProps
>(({
  className,
  options,
  label,
  description,
  size = 'default',
  error,
  required = false,
  value,
  defaultValue,
  onValueChange,
  ...props
}, ref) => {
  const sizeClass = sizeVariants[size]
  const [internalValue, setInternalValue] = React.useState(value || defaultValue || '')

  // Update internal value when controlled value changes
  React.useEffect(() => {
    if (value !== undefined) {
      setInternalValue(value || '')
    }
  }, [value])

  const handleValueChange = (newValue: string) => {
    setInternalValue(newValue)
    onValueChange?.(newValue)
  }

  return (
    <UIBox className="space-y-4">
      {label && (
        <UILabel className={cn(
          "font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70",
          sizeClass.label,
          required && "after:content-['*'] after:ml-0.5 after:text-destructive",
          error && "text-destructive"
        )}>
          {label}
        </UILabel>
      )}
      {description && (
        <UITypography
          variant="p"
          color="muted"
          className={sizeClass.mainDescription}
        >
          {description}
        </UITypography>
      )}

      <RadioGroup
        ref={ref}
        className={cn("space-y-3", className)}
        value={internalValue}
        onValueChange={handleValueChange}
        {...props}
      >
        {options.map((option, index) => {
          const itemId = option.id || `${props.name || 'card-radio'}-${option.value}-${index}`
          const isSelected = internalValue === option.value

          return (
            <UIBox key={option.value} className="space-y-0">
              <RadioGroupItem
                value={option.value}
                id={itemId}
                disabled={option.disabled}
                className="sr-only"
              />
              <UILabel
                htmlFor={itemId}
                className={cn(
                  "block cursor-pointer rounded-lg border-2 transition-[background-color,border-color,box-shadow] hover:bg-accent/5 relative",
                  sizeClass.card,
                  option.disabled && "opacity-50 cursor-not-allowed",
                  error && "border-destructive",
                  isSelected
                    ? "border-primary bg-primary/5 shadow-sm"
                    : "border-muted hover:border-muted-foreground/20"
                )}
              >
                <UIBox className="flex items-start gap-4 w-full">
                  {option.icon && (
                    <UIBox className={cn(
                      "flex-shrink-0 mt-1",
                      isSelected ? "text-primary" : "text-muted-foreground",
                      sizeClass.icon
                    )}>
                      {option.icon}
                    </UIBox>
                  )}

                  <UIBox className="flex-1 min-w-0">
                    <UIBox className="flex items-start justify-between gap-4">
                      <UIBox className="flex-1 min-w-0">
                        <UIBox className={cn(
                          "font-semibold",
                          isSelected ? "text-primary" : "text-foreground",
                          sizeClass.title
                        )}>
                          {option.title}
                        </UIBox>
                        {option.description && (
                          <UITypography
                            variant="p"
                            color="muted"
                            className={cn("mt-1", sizeClass.description)}
                          >
                            {option.description}
                          </UITypography>
                        )}
                      </UIBox>

                      <UIBox className="flex flex-col items-end gap-2 flex-shrink-0">
                        {option.badge && (
                          <UIBox className="rounded-full bg-primary/10 px-2 py-1 text-xs font-medium text-primary whitespace-nowrap">
                            {option.badge}
                          </UIBox>
                        )}
                        {option.price && (
                          <UIBox className={cn(
                            "font-bold text-foreground whitespace-nowrap",
                            sizeClass.price
                          )}>
                            {option.price}
                          </UIBox>
                        )}
                      </UIBox>
                    </UIBox>
                  </UIBox>

                  {/* Selected indicator */}
                  <UIBox className={cn(
                    "flex-shrink-0 w-5 h-5 rounded-full border-2 transition-colors mt-1 flex items-center justify-center",
                    isSelected ? "border-primary bg-primary" : "border-muted-foreground"
                  )}>
                    {isSelected && (
                      <UIBox className="w-3 h-3 rounded-full bg-primary-foreground" />
                    )}
                  </UIBox>
                </UIBox>
              </UILabel>
            </UIBox>
          )
        })}
      </RadioGroup>

      {error && (
        <UITypography
          variant="p"
          color="destructive"
          className={cn("animate-in slide-in-from-top-1 duration-200", sizeClass.error)}
          role="alert"
        >
          {error}
        </UITypography>
      )}
    </UIBox>
  )
})

UIRadioGroupCard.displayName = "UIRadioGroupCard"